import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import { useParams } from 'react-router-dom'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import AddButton from '../../components/ui/AddButton/AddButton'
import AddTeamModal from '../../components/ui/AddTeamModal/AddTeamModal'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import useHistoryPush from '../../hooks/useHistoryPush'
import getOrgClub from '../../Apis/clubs/getOrgClub'
import getClubTeams from '../../Apis/clubs/getClubTeams'
import getSeasonsByTeam from '../../Apis/season/getSeasonsByTeam'
import addTeam from '../../Apis/team/addTeam'
import updateTeam from '../../Apis/team/updateTeam'
import deleteTeam from '../../Apis/team/deleteTeam'
import SimpleDeleteModal from '../../components/ui/SimpleDeleteModal/SimpleDeleteModal'
import QueryKeys from '../../utils/constants/queryKeys'
import clubPageStyles from './clubPage.styles'
import DefaultOrgIcon from '../../assets/icons/DefaultOrgIcon'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import IsPermitted from '../../components/IsPermitted/IsPermitted'

const useStyles = makeStyles(clubPageStyles)

// Short labels keyed by the backend enum ids (Genders / AgeRestrictions). "Not Specific" and
// "No Restriction" show as a dash; unknown ids fall back to the backend label.
const GENDER_SHORT_LABELS = { 1: 'Male', 2: 'Female', 3: '-' }
const AGE_RESTRICTION_SHORT_LABELS = {
  1: '-',
  2: 'U22',
  3: 'U21',
  4: 'U20',
  5: 'U19',
  6: 'U18',
  7: 'U17',
  8: 'U16',
  9: 'U15',
  10: 'U14',
  11: 'Junior',
  12: 'Mini',
  13: 'Academy',
}

const shortLabel = (labels, value) => labels[value?.id] ?? value?.name ?? '-'

const ClubPage = () => {
  const classes = useStyles()
  const { clubId } = useParams()
  const { org: orgId } = useUrlSearchParams(['org']) || {}
  const { redirect } = useHistoryPush()
  const queryClient = useQueryClient()

  const [showAddTeamModal, setShowAddTeamModal] = useState(false)
  const [editingTeam, setEditingTeam] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [teamToDelete, setTeamToDelete] = useState(null)

  const { data: club, isLoading: isClubLoading } = useQuery(
    [QueryKeys.GET_ORG_CLUB, orgId, clubId],
    () => getOrgClub(orgId, clubId),
    { enabled: !!orgId && !!clubId }
  )

  const { data: teams, isLoading: isTeamsLoading } = useQuery(
    [QueryKeys.GET_CLUB_TEAMS, orgId, clubId],
    () => getClubTeams(orgId, clubId),
    { enabled: !!orgId && !!clubId }
  )

  // One request per team: the teams list carries no season info. Same key as the team
  // statistics page, so the cache is shared.
  const seasonsQueries = useQueries(
    (teams || []).map((team) => ({
      queryKey: [QueryKeys.GET_SEASONS_BY_TEAM, orgId, team.id],
      queryFn: () => getSeasonsByTeam({ orgId, teamId: team.id }),
      enabled: !!orgId,
    }))
  )

  const renderSeasons = (index) => {
    const { data: seasons, isLoading, isError } = seasonsQueries[index] || {}
    if (isLoading) return '…'
    if (isError || !seasons?.length) return '—'
    return [...seasons]
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      .map((season) => (
        <Chip key={season.id} label={season.name} size='small' className={classes.seasonChip} />
      ))
  }

  const invalidateTeams = () => {
    queryClient.invalidateQueries([QueryKeys.GET_CLUB_TEAMS, orgId, clubId])
    queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
  }

  const addClubTeam = useMutation(
    (data) => addTeam({ ...data, orgId, clubId }),
    {
      onSuccess: () => {
        invalidateTeams()
        setShowAddTeamModal(false)
      },
      onError: () => {
        setShowAddTeamModal(false)
      },
    }
  )

  const updateClubTeam = useMutation(
    (data) => updateTeam({ ...data, orgId }),
    {
      onSuccess: () => {
        invalidateTeams()
        setShowAddTeamModal(false)
        setEditingTeam(null)
      },
      onError: () => {
        setShowAddTeamModal(false)
        setEditingTeam(null)
      },
    }
  )

  const deleteClubTeam = useMutation(
    (data) => deleteTeam({ ...data, orgId }),
    {
      onSuccess: () => {
        invalidateTeams()
        setShowDeleteModal(false)
        setTeamToDelete(null)
      },
      onError: () => {
        setShowDeleteModal(false)
        setTeamToDelete(null)
      },
    }
  )

  const handleEditTeam = (team) => {
    // Map TeamDetailsDto fields to the season-team format that AddTeamModal expects
    setEditingTeam({
      teamId: team.id,
      teamName: team.name,
      threeLetterIdentifier: team.threeLetterIdentifier,
      genderValue: team.gender?.name,
      ageRestrictionValue: team.ageRestriction?.name,
      color: team.color,
      externalId: team.externalId,
      teamLogoUrl: team.logoUrl,
      clubId: team.clubId,
    })
    setShowAddTeamModal(true)
  }

  const handleCloseTeamModal = () => {
    setShowAddTeamModal(false)
    setEditingTeam(null)
  }

  const handleDeleteTeam = (team) => {
    setTeamToDelete(team)
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    setTeamToDelete(null)
  }

  const onConfirmDeleteTeam = () => {
    if (teamToDelete) {
      deleteClubTeam.mutate({ teamId: teamToDelete.id })
    }
  }

  return (
    <Card className={classes.card}>
      <div className={classes.clubHeader}>
        {isClubLoading ? (
          <LoaderDots />
        ) : (
          <>
            {club?.logoUrl ? (
              <img
                src={club.logoUrl}
                alt={club.name}
                className={classes.clubLogo}
              />
            ) : (
              <DefaultOrgIcon style={{ width: 80, height: 80 }} />
            )}
            <Typography variant='h5' className={classes.clubName}>
              {club?.name}
            </Typography>
          </>
        )}
      </div>

      <Divider />

      <CardHeader
        title={
          <div className={classes.headerContainer}>
            <Typography className={classes.cardTitle} variant='h6'>
              Teams
            </Typography>
            <IsPermitted permission={USER_PERMISSIONS.CREATE_TEAM}>
              <AddButton label='Add Team' onClick={() => setShowAddTeamModal(true)} filled />
            </IsPermitted>
          </div>
        }
      />
      <CardContent>
        {isTeamsLoading ? (
          <LoaderDots />
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Team Name</TableCell>
                  <TableCell>Identifier</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Seasons</TableCell>
                  <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!teams || teams.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align='center'>No teams found</TableCell>
                  </TableRow>
                )}
                {teams && teams.map((team, index) => (
                  <TableRow
                    key={team.id}
                    className={classes.clickableRow}
                    onClick={() => redirect(`/seasonTeams/${team.id}`)}
                  >
                    <TableCell>
                      <div className={classes.teamNameCell}>
                        {team.logoUrl && (
                          <img
                            src={team.logoUrl}
                            alt={team.name}
                            className={classes.teamLogo}
                          />
                        )}
                        {team.name}
                      </div>
                    </TableCell>
                    <TableCell>{team.threeLetterIdentifier}</TableCell>
                    <TableCell>{shortLabel(GENDER_SHORT_LABELS, team.gender)}</TableCell>
                    <TableCell>{shortLabel(AGE_RESTRICTION_SHORT_LABELS, team.ageRestriction)}</TableCell>
                    <TableCell>{renderSeasons(index)}</TableCell>
                    <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>
                      <IsPermitted permission={USER_PERMISSIONS.UPDATE_TEAM}>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditTeam(team)
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </IsPermitted>
                      <IsPermitted permission={USER_PERMISSIONS.DELETE_TEAM}>
                        <IconButton
                          size='small'
                          color='secondary'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteTeam(team)
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </IsPermitted>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <AddTeamModal
        showAddTeamModal={showAddTeamModal}
        handleCloseModal={handleCloseTeamModal}
        addTeam={addClubTeam}
        updateTeam={updateClubTeam}
        teamData={editingTeam}
      />

      <SimpleDeleteModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        text={`Are you sure you want to delete "${teamToDelete?.name}"?`}
        onConfirm={onConfirmDeleteTeam}
        isLoading={deleteClubTeam.isLoading}
      />
    </Card>
  )
}

export default ClubPage
